/**
 * PlanConfirmationCard body.
 *
 * Read-only. v0 spec §5.1 — no editable fields on this card. Free-text
 * framed_request changes route through /v2/messages (Owner path).
 * scope_envelope changes happen via the Scope Panel.
 *
 * Card schema: { tool, framed_request, rationale?, scope_envelope }.
 */
import React from 'react';
import { Box, Stack, Typography } from '@mui/material';

import { Field } from '../shared/Field';
import { ScopeBlock } from '../shared/ScopeBlock';

interface PlanConfirmationBodyProps {
  content: Record<string, unknown>;
}

export const PlanConfirmationBody: React.FC<PlanConfirmationBodyProps> = ({
  content,
}) => {
  const tool = String(content.tool ?? '');
  const framedRequest = String(content.framed_request ?? '');
  const rationale = content.rationale ? String(content.rationale) : null;
  const scope = content.scope_envelope;

  return (
    <Stack spacing={1.5}>
      <Field label="Tool" value={toolDisplay(tool)} />
      <Field
        label="Framed request"
        value={
          <Box
            component="span"
            sx={{
              fontStyle: 'italic',
              color: 'text.primary',
            }}
          >
            &ldquo;{framedRequest}&rdquo;
          </Box>
        }
      />
      {rationale ? <Field label="Rationale" value={rationale} /> : null}

      <Box>
        <Typography
          variant="caption"
          sx={{
            fontSize: 12,
            fontWeight: 500,
            color: 'text.secondary',
            letterSpacing: '0.02em',
            textTransform: 'uppercase',
            display: 'block',
            mb: 0.5,
          }}
        >
          Scope (frozen for this plan)
        </Typography>
        <ScopeBlock scope={scope} />
      </Box>
    </Stack>
  );
};

function toolDisplay(tool: string): string {
  switch (tool) {
    case 'edit_scenarios':
      return 'Edit Scenarios';
    case 'chart_visualization':
      return 'Chart Visualization';
    case 'driver_analysis':
      return 'Driver Analysis';
    case 'extracts':
      return 'Extracts';
    default:
      return tool;
  }
}
